import { OrganizationService } from '@/api/';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import {
  ArchiveRestore,
  ChevronLeft,
  ChevronRight,
  Info,
  Loader2,
  MoveRight,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Organization } from './Organization';
import { Workspace } from './Workspace';

export const Organizations = () => {
  // State for handling deletion confirmation popup
  const [orgToDelete, setOrgToDelete] = useState<{
    id: string;
    name: string;
    isDeleted?: boolean;
    createdBy?: { name: string };
  } | null>(null);

  const user = useStore($user);
  const orgLogoUrls = useStore($orgLogoUrls);

  // Fetch active organizations only
  const orgs = useQuery({
    queryKey: ['getOrganizations'],
    queryFn: async () => {
      const res = await OrganizationService.getOrganizations();
      return res;
    },
  });

  // Fetch archived organizations (not shown in UI)
  const archivedOrgs = useQuery({
    queryKey: ['getArchivedOrganizations'],
    queryFn: async () => {
      const res = await OrganizationService.getArchivedOrganizations();
      return res;
    },
    enabled: false,
  });

  const selectedOrg = useStore($orgInSettings);
  const selectedWorkspace = useStore($workspaceInSettings);
  const organizationSelected = useStore($orgnizationSelected);
  const workspaceSelected = useStore($workspaceSelected);

  useEffect(() => {
    if (orgs.data) {
      orgs.data.forEach(org => {
        ensureOrgLogoUrl(org.id).catch(error => {
          console.error(`Error fetching logo for orgId: ${org.id}`, error);
        });
      });
    }
  }, [orgs.data]);

  useEffect(() => {
    $orgnizationSelected.set(false);
    $workspaceSelected.set(false);
  }, []);

  function goback() {
    if (organizationSelected && workspaceSelected) {
      $workspaceSelected.set(false);
    } else if (organizationSelected) {
      $orgnizationSelected.set(false);
    }
  }

  // Executes deletion after user confirmation
  function handleConfirmDelete(org: {
    id: string;
    name: string;
    isDeleted?: boolean;
    createdBy?: { name: string };
  }) {
    OrganizationService.hideOrganization(org.id)
      .then(() => {
        orgs.refetch();
        archivedOrgs.refetch();
        setOrgToDelete(null);
      })
      .catch(error => {
        console.error(error);
        setOrgToDelete(null);
      });
  }

  const visibleOrgs = orgs.data;
  const isLoading = orgs.isLoading;

  return (
    <div>
      <div id="title">
        <div className="flex flex-1 items-center justify-start">
          {organizationSelected && (
            <Button
              variant="outline"
              size="icon"
              className="mr-2 h-6 w-6"
              onClick={goback}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
          {organizationSelected && !workspaceSelected ? (
            <>
              <span className="text-xl font-semibold tracking-tight">
                Organizations
              </span>
              <ChevronRight />
              <span>{selectedOrg?.name}</span>
            </>
          ) : organizationSelected && workspaceSelected ? (
            <>
              <span className="text-xl font-semibold tracking-tight">
                Organizations
              </span>
              <ChevronRight />
              <span>{selectedOrg?.name}</span>
              <ChevronRight />
              <span>Workspaces</span>
              <ChevronRight />
              <span>{selectedWorkspace?.workspace?.name}</span>
            </>
          ) : (
            <span className="text-xl font-semibold tracking-tight">
              Organizations
            </span>
          )}
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          View and manage your{' '}
          {workspaceSelected ? 'workspace' : 'organization'} settings
        </p>
      </div>

      {organizationSelected && !workspaceSelected ? (
        <div className="mt-6">
          <Organization />
        </div>
      ) : organizationSelected && workspaceSelected ? (
        <div className="mt-6">
          <Workspace />
        </div>
      ) : (
        <div>
          {isLoading ? (
            <div className="flex h-full w-full flex-1 items-center justify-center">
              <Loader2 className="animate-spin" />
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-270px)] pr-3">
              <div className="flex w-full flex-col gap-3">
                {visibleOrgs?.map(org => (
                  <button
                    key={org.id}
                    className={cn(
                      'flex flex-col items-start gap-2 rounded-lg border p-4 text-left text-sm transition-all hover:bg-accent',
                      selectedOrg?.id === org.id && 'bg-muted',
                    )}
                    onClick={() => {
                      $orgInSettings.set(org);
                      $orgnizationSelected.set(true);
                    }}
                  >
                    <div className="flex w-full items-center gap-4">
                      <Avatar className="h-10 w-10 rounded-md">
                        <AvatarImage src={orgLogoUrls[org.id]?.url || ''} />
                        <AvatarFallback>
                          {org.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex w-full flex-col gap-1">
                        <div className="flex flex-1 items-center">
                          <div className="flex flex-col items-start justify-center">
                            <div className="text-sm font-semibold">
                              {org.name}
                            </div>
                            {org.createdBy && (
                              <div className="text-muted-foreground">
                                Created by {org.createdBy.name}
                              </div>
                            )}
                          </div>
                          <div className="ml-auto flex items-center gap-2 p-1 hover:rounded-md hover:bg-muted">
                            {org.isDeleted ? (
                              <span title="Organization is archived. Click to unarchive.">
                                <ArchiveRestore
                                  className="h-5 w-5 cursor-pointer text-red-500"
                                  onClick={e => {
                                    e.stopPropagation();
                                    OrganizationService.unhideOrganization(
                                      org.id,
                                    )
                                      .then(() => {
                                        orgs.refetch();
                                        archivedOrgs.refetch();
                                      })
                                      .catch(console.error);
                                  }}
                                />
                              </span>
                            ) : (
                              <>
                                {orgs.data && orgs.data.length > 1 ? (
                                  <span title="Organization is active. Click to Delete.">
                                    <Trash2
                                      className="h-4 w-4 cursor-pointer text-red-600"
                                      onClick={e => {
                                        e.stopPropagation();
                                        // Open confirmation popup before deletion
                                        setOrgToDelete(org);
                                      }}
                                    />
                                  </span>
                                ) : (
                                  <span
                                    title="At least one organization must be present."
                                    className="cursor-not-allowed opacity-50"
                                  >
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                  </span>
                                )}
                              </>
                            )}
                            <MoveRight />
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      )}

      {/* Confirmation Popup */}
      {orgToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-[600px] rounded-md bg-white p-6">
            <h3 className="mb-4 text-lg font-semibold">Confirm Deletion</h3>
            <p className="mb-4">
              Are you sure you want to delete organization{' '}
              <strong>{orgToDelete.name}</strong>?
            </p>

            <p className="mb-4 flex items-center ">
              <Info className="mr-1 h-4 w-4" />
              This action is permanent and cannot be undone.
            </p>

            <ul className="mb-4 ml-14 list-disc space-y-1 text-sm text-muted-foreground">
              <li>
                All associated <strong>data</strong> will be permanently
                deleted.
              </li>
              <li>
                Any <strong>workflows</strong>, <strong>projects</strong>, and{' '}
                <strong>resources</strong> will be removed.
              </li>
              <li>
                Unenrollment from the current <strong>billing plan</strong>.
              </li>
              <li>
                All <strong>members</strong> will lose access to this
                organization.
              </li>
            </ul>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setOrgToDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleConfirmDelete(orgToDelete)}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
